/**
 * Isomorphic utility for validating and sanitizing returnTo URLs.
 * Used by both SSR controllers and client-side auth components.
 */

const ROLE_RESTRICTED_PREFIXES = { '/admin': 'admin' };
const AUTH_PAGES = ['/login', '/signup', '/forgot-password'];

/**
 * Returns a safe, authorized return URL — or '/' if the value is
 * missing, malformed, an open redirect, an auth-page loop, or
 * targets a page the user's role doesn't permit.
 *
 * @param {string|null|undefined} returnTo - Raw returnTo query param value
 * @param {object|null} user - Authenticated user object (needs .role)
 * @returns {string} Validated path to navigate to
 */
export function getAuthorizedReturnUrl(returnTo, user) {
    if (!returnTo) return '/';

    let decoded;
    try {
        decoded = decodeURIComponent(returnTo);
    } catch {
        return '/';
    }

    // Prevent open redirects
    if (!decoded.startsWith('/') || decoded.startsWith('//')) return '/';

    // Prevent auth-page loops
    if (AUTH_PAGES.some(p => decoded.startsWith(p))) return '/';

    // Check role restrictions
    for (const [prefix, role] of Object.entries(ROLE_RESTRICTED_PREFIXES)) {
        if (decoded.startsWith(prefix) && user?.role !== role) return '/';
    }

    return decoded;
}
